import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BrandLogo, PickfoodMark } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { ASSETS } from "../lib/assets";
import { Store, Zap, Globe, Printer, Bell, CreditCard, Check, ArrowRight, ArrowUpRight, Clock, MapPin } from "lucide-react";

const features = [
  { icon: Store, title: "Multi-sede & Multi-tenant", text: "Un solo codice, tanti locali. Dati, menu e ordini isolati per ogni attività." },
  { icon: Globe, title: "White-label", text: "Dominio, logo, colori e PWA con il marchio del tuo cliente." },
  { icon: Zap, title: "Ordini in tempo reale", text: "La cassa si aggiorna all'istante, con avvisi sonori e visivi." },
  { icon: CreditCard, title: "Pagamenti myPOS", text: "Incassi diretti sul conto dell'esercente. Sandbox e produzione." },
  { icon: Printer, title: "Stampa 80mm", text: "Comanda termica, ristampa e anteprima, provider estendibile." },
  { icon: Bell, title: "Web Push", text: "Nessun ordine perso: avvisi persistenti finché non gestiti." },
];

const plans = [
  { code: "standard", name: "Standard", price: "29", feats: ["1 sede", "3 operatori", "500 ordini/mese", "Dicitura Pickfood"] },
  { code: "custom", name: "Personalizzato", price: "59", feats: ["2 sedi", "8 operatori", "2.000 ordini/mese", "Statistiche avanzate", "Delivery incluso"], hot: true },
  { code: "white_label", name: "Full White-Label", price: "99", feats: ["5 sedi", "Operatori illimitati", "Ordini illimitati", "Dominio personalizzato", "Nessun marchio Pickfood"] },
];

const steps = [
  { n: "01", t: "Crea il locale", d: "Il super admin genera il tenant: brand, menu, dominio in pochi minuti." },
  { n: "02", t: "Il cliente ordina", d: "Menu digitale, personalizzazione prodotto, ritiro o consegna." },
  { n: "03", t: "Tu prepari", d: "Ordini in tempo reale in cassa, stampa comanda, stato al cliente." },
];

const marquee = ["Panini", "Hamburger", "Piadine", "Fritti", "Bevande", "Dolci", "Bowl", "Vegano", "Pizza", "Caffè"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A] overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <BrandLogo />
          <div className="flex items-center gap-2">
            <Link to="/t/paninoteca-demo"><Button variant="ghost" data-testid="nav-demo" className="rounded-full font-semibold">Vedi demo</Button></Link>
            <Link to="/login"><Button data-testid="nav-login" className="brand-bg hover:opacity-90 rounded-full font-semibold">Accedi</Button></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-5 pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] bg-[#1A1A1A] text-[#FDFBF7] px-3 py-1.5 rounded-full">
                <PickfoodMark size={16} color="#FF7A00" /> SaaS per la ristorazione
              </span>
              <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] mt-6">
                Ordini food,<br />
                <span className="relative inline-block">
                  <span className="brand-text">senza attese.</span>
                  <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10" preserveAspectRatio="none"><path d="M2 8 C80 2 220 2 298 6" stroke="#FF7A00" strokeWidth="4" fill="none" strokeLinecap="round"/></svg>
                </span>
              </h1>
              <p className="mt-7 text-lg text-[#1A1A1A]/70 max-w-md">
                La web app che porta online paninoteche, hamburgerie e bar.
                Menu digitale, prenotazione, pagamento e stampa. Tutto dal browser.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/t/paninoteca-demo">
                  <Button data-testid="hero-order" className="h-14 px-8 text-base brand-bg hover:opacity-90 rounded-full font-semibold group">
                    Prova lo storefront <ArrowRight size={18} className="ml-1 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                </Link>
                <a href="#pricing"><Button variant="outline" className="h-14 px-8 text-base rounded-full font-semibold border-[#1A1A1A]/15">Piani & prezzi</Button></a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-[#1A1A1A]/60">
                <span className="flex items-center gap-1.5"><Check size={16} className="brand-text" /> Ritiro & Delivery</span>
                <span className="flex items-center gap-1.5"><Check size={16} className="brand-text" /> Multi-tenant</span>
                <span className="flex items-center gap-1.5"><Check size={16} className="brand-text" /> White-label</span>
              </div>
            </motion.div>
          </div>

          {/* device mock */}
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="relative">
            <div className="absolute -top-6 -right-2 w-40 h-40 rounded-full brand-bg opacity-20 blur-2xl" />
            <div className="relative mx-auto w-[300px] rounded-[2.2rem] bg-[#1A1A1A] p-3 shadow-[0_30px_80px_rgb(0,0,0,0.28)]">
              <div className="rounded-[1.7rem] overflow-hidden bg-[#FDFBF7]">
                <div className="brand-bg px-4 pt-6 pb-4">
                  <div className="flex items-center justify-between">
                    <BrandLogo name="Paninoteca Bigfoot" color="#fff" size={26} className="text-white" />
                    <span className="text-[10px] font-bold bg-white/25 text-white px-2 py-0.5 rounded-full">Aperto</span>
                  </div>
                  <div className="flex gap-3 text-white/85 text-[11px] mt-3">
                    <span className="flex items-center gap-1"><Clock size={11}/>~20 min</span>
                    <span className="flex items-center gap-1"><MapPin size={11}/>Torino</span>
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  {[["Panino Bigfoot", "8,50", ASSETS.burgers[0]], ["Cheese Burger", "9,00", ASSETS.burgers[2]], ["Patatine Fritte", "3,50", ASSETS.sandwiches[2]]].map(([n, p, img], i) => (
                    <div key={i} className="flex items-center gap-3 bg-white border rounded-xl p-2">
                      <img src={img} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div className="flex-1"><p className="text-xs font-semibold">{n}</p><p className="text-xs brand-text font-bold">€ {p}</p></div>
                      <span className="w-6 h-6 rounded-full brand-bg text-white flex items-center justify-center text-sm">+</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 pt-0">
                  <div className="brand-bg text-white rounded-full h-11 flex items-center justify-between px-5 text-sm font-semibold">
                    <span>3 articoli</span><span>€ 21,00</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* marquee */}
        <div className="border-y border-black/5 bg-white/60 py-3 overflow-hidden">
          <div className="flex gap-8 whitespace-nowrap animate-[marquee_22s_linear_infinite]">
            {[...marquee, ...marquee].map((m, i) => (
              <span key={i} className="font-head font-bold text-lg text-[#1A1A1A]/30 flex items-center gap-8">{m}<PickfoodMark size={14} color="#FF7A00" /></span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES bento */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]/40">Perché Pickfood</p>
              <h2 className="font-head text-3xl sm:text-5xl font-extrabold tracking-tight mt-2 max-w-xl">Una piattaforma, non un semplice menu.</h2>
            </div>
            <Link to="/login" className="hidden sm:block"><Button variant="outline" className="rounded-full font-semibold border-[#1A1A1A]/15">Area riservata <ArrowUpRight size={16} className="ml-1"/></Button></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className={`group rounded-3xl p-7 border transition-colors ${i === 0 ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "bg-white border-black/8 hover:border-[var(--brand)]"}`}>
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${i === 0 ? "bg-white/10" : "brand-soft"}`}>
                  <f.icon className={i === 0 ? "text-[#FF7A00]" : "brand-text"} size={22} />
                </div>
                <h3 className="font-head font-bold text-xl">{f.title}</h3>
                <p className={`text-sm mt-2 ${i === 0 ? "text-white/70" : "text-[#1A1A1A]/60"}`}>{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="relative pl-4">
              <span className="font-head text-6xl font-extrabold brand-text/20 text-[#FF7A00]/20">{s.n}</span>
              <h3 className="font-head text-xl font-bold -mt-6">{s.t}</h3>
              <p className="text-sm text-[#1A1A1A]/60 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING (dark) */}
      <section id="pricing" className="mt-14 bg-[#1A1A1A] text-[#FDFBF7] py-20 rounded-t-[3rem]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">Abbonamenti</p>
            <h2 className="font-head text-3xl sm:text-5xl font-extrabold tracking-tight mt-2">Scegli il piano del tuo cliente</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p) => (
              <div key={p.code} data-testid={`plan-${p.code}`}
                className={`rounded-3xl p-7 border ${p.hot ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-white/5 border-white/10"}`}>
                {p.hot && <div className="bg-black/25 text-xs font-bold px-3 py-1 rounded-full inline-block mb-3">PIÙ SCELTO</div>}
                <h3 className="font-head text-xl font-bold">{p.name}</h3>
                <div className="mt-3 mb-5"><span className="font-head text-5xl font-extrabold">€{p.price}</span><span className={p.hot ? "text-white/80" : "text-white/50"}>/mese</span></div>
                <ul className="space-y-2.5 mb-7">
                  {p.feats.map((ft, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm"><Check size={17} className={`mt-0.5 shrink-0 ${p.hot ? "text-white" : "text-[#FF7A00]"}`} />{ft}</li>
                  ))}
                </ul>
                <Link to="/login"><Button className={`w-full h-12 rounded-full font-semibold ${p.hot ? "bg-white text-[#1A1A1A] hover:bg-white/90" : "brand-bg hover:opacity-90"}`}>Inizia</Button></Link>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 border-t border-white/10 pt-10">
            <BrandLogo color="#FF7A00" className="text-white" />
            <p className="text-sm text-white/50">www.pickfood.it · EUR · Europe/Rome</p>
            <Link to="/login" className="text-sm text-white/70 underline underline-offset-4">Area riservata</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
